import { useQuery } from "@apollo/client";
import { Search } from "@mui/icons-material";
import {
  MenuItem,
  Box,
  Autocomplete,
  ListItemButton,
  CircularProgress,
  InputAdornment,
  Alert,
} from "@mui/material";
import { Field, Form, Formik, useField, useFormikContext } from "formik";
import _ from "lodash";
import { ReactElement, useState } from "react";
import * as Yup from "yup";

import {
  GcsImportIntegration,
  CampaignContactGcsImportArgs,
  GetGcsImportFileNameCompletionsQuery,
  GetGcsImportFileNameCompletionsQueryVariables,
  GetGcsImportFileHeadersQuery,
  GetGcsImportFileHeadersQueryVariables,
} from "~frontend/__generated__/graphql";
import RouteLeavingGuard from "~frontend/components/RouteLeavingGuard";
import { getDefaultPhoneNumberColumn } from "~frontend/components/forms/CsvUpload/ParsedFile";
import { FormikSelect } from "~frontend/components/forms/FormSelect";
import FormTextField from "~frontend/components/forms/FormTextField";
import FormikErrorBanner from "~frontend/components/forms/FormikErrorBanner";
import {
  GET_GCS_IMPORT_FILE_HEADERS,
  GET_GCS_IMPORT_FILE_NAME_COMPLETIONS,
} from "~frontend/graphql/integration";
import theme, { palette } from "~frontend/theme";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useDebounce from "~frontend/util/useDebounce";

import ContactImportFormSubmitButtons from "../ContactImportFormSubmitButtons";

const MAX_SEARCH_RESULTS = 100;
const OVER_LIMIT_MESSAGE = `More than ${MAX_SEARCH_RESULTS} results found. Please refine your search.`;

function ObjectNameInput({
  integrationId,
  readOnly,
}: {
  integrationId: string;
  readOnly?: boolean;
}) {
  const [field, meta, helpers] = useField<string>("objectName");

  const [search, setSearch] = useState<string>("");
  const debouncedSearch = useDebounce(search, 500);
  const searchQuery = useQuery<
    GetGcsImportFileNameCompletionsQuery,
    GetGcsImportFileNameCompletionsQueryVariables
  >(GET_GCS_IMPORT_FILE_NAME_COMPLETIONS, {
    variables: {
      input: {
        integrationId,
        search: debouncedSearch,
      },
    },
    skip: debouncedSearch.length < 3 || readOnly,
    fetchPolicy: "network-only",
  });

  let options: string[] = [];
  let noOptionsText: string = "No files found";
  if (searchQuery.loading) {
    noOptionsText = "Loading...";
  } else if (searchQuery.error) {
    noOptionsText = "Error searching for GCS files";
  } else if (search.length < 3) {
    noOptionsText = "Enter at least 3 characters to search...";
  } else if (searchQuery.data) {
    options = searchQuery.data.getGCSImportFileNameCompletions;
    if (options.length > MAX_SEARCH_RESULTS) {
      options = options.slice(0, MAX_SEARCH_RESULTS);
      options.push(OVER_LIMIT_MESSAGE);
    }
  }

  const loading =
    search.length >= 3 && (searchQuery.loading || debouncedSearch !== search);

  return (
    <Autocomplete<string>
      sx={{ flex: 1 }}
      value={field.value}
      onChange={(_, newValue) => {
        helpers.setTouched(true).catch(console.error);
        helpers.setValue(newValue || "").catch(console.error);
      }}
      inputValue={search}
      onInputChange={(_, newSearch) => setSearch(newSearch)}
      options={options}
      noOptionsText={noOptionsText}
      getOptionDisabled={option => option === OVER_LIMIT_MESSAGE}
      renderOption={(props, option) => {
        return (
          <ListItemButton
            key={option}
            {...(props as unknown as React.HTMLAttributes<HTMLDivElement>)}
          >
            {option}
          </ListItemButton>
        );
      }}
      getOptionLabel={option => option}
      getOptionKey={option => option}
      filterOptions={options => options}
      loading={loading}
      loadingText="Loading..."
      disabled={readOnly}
      renderInput={params => (
        <FormTextField
          {...params}
          label="GCS File Name"
          error={meta.touched && Boolean(meta.error)}
          helperText={meta.touched && meta.error}
          fullWidth
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment
                  position="start"
                  sx={{ padding: theme.spacing(1) }}
                >
                  <Search />
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress
                    color="inherit"
                    size={20}
                    sx={{ marginRight: "64px" }}
                  />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
            sx: {
              border: `1px solid ${palette.midGray} !important`,
              fontSize: "14px",
              padding: "2.5px !important",
            },
            inputProps: {
              ...params.inputProps,
              sx: {
                padding: "7.5px 4px 7.5px 5px !important",
                marginRight: "50px",
              },
            },
          }}
        />
      )}
    />
  );
}

function ColumnFields({
  integrationId,
  objectName,
  showVanIdColumn,
  readOnly,
}: {
  integrationId: string;
  objectName?: string;
  showVanIdColumn: boolean;
  readOnly?: boolean;
}): ReactElement {
  const formatError = useErrorMessageFormatter();

  const { values, setFieldValue } =
    useFormikContext<CampaignContactGcsImportArgs>();

  const headersQuery = useQuery<
    GetGcsImportFileHeadersQuery,
    GetGcsImportFileHeadersQueryVariables
  >(GET_GCS_IMPORT_FILE_HEADERS, {
    variables: {
      input: {
        integrationId,
        objectName: objectName || "",
      },
    },
    skip: !objectName || readOnly,
    fetchPolicy: "network-only",
    onCompleted: data => {
      if (!values.phoneNumberColumn) {
        // auto-select the phone number column
        const phoneNumberColumn = getDefaultPhoneNumberColumn({
          columns: data.getGCSImportFileHeaders,
        });
        if (phoneNumberColumn) {
          setFieldValue("phoneNumberColumn", phoneNumberColumn).catch(
            console.error
          );
        }
      }
    },
  });

  let renderValue: (value: unknown) => React.ReactNode = value => String(value);
  let inputsDisabled = readOnly;
  if (!objectName) {
    renderValue = () => "Select a file to see columns";
    inputsDisabled = true;
  } else if (headersQuery.loading) {
    renderValue = () => "Loading...";
    inputsDisabled = true;
  } else if (headersQuery.error) {
    renderValue = () => "Error loading columns";
    inputsDisabled = true;
  }

  return (
    <>
      {headersQuery.error && (
        <Alert severity="error">
          Error loading columns: {formatError(headersQuery.error)}
        </Alert>
      )}
      <FormikSelect
        label="Phone Number Column"
        name="phoneNumberColumn"
        fullWidth
        disabled={inputsDisabled}
        renderValue={renderValue}
      >
        {headersQuery.data?.getGCSImportFileHeaders.map(header => (
          <MenuItem value={header} key={header}>
            {header}
          </MenuItem>
        ))}
      </FormikSelect>

      {showVanIdColumn && (
        <FormikSelect
          label="VAN ID Column"
          name="vanIdColumn"
          fullWidth
          disabled={inputsDisabled}
          renderValue={renderValue}
        >
          {headersQuery.data?.getGCSImportFileHeaders.map(header => (
            <MenuItem value={header} key={header}>
              {header}
            </MenuItem>
          ))}
        </FormikSelect>
      )}
    </>
  );
}

export default function ImportForm({
  onSubmit,
  availableGCSIntegrations,
  initialValues,
  readOnly,
  resetContactSourceSettings,
  ngpVanCampaignIntegrationEnabled,
}: {
  onSubmit?: (values: any) => void;
  availableGCSIntegrations: GcsImportIntegration[];
  initialValues: CampaignContactGcsImportArgs;
  readOnly?: boolean;
  resetContactSourceSettings: (() => void) | null;
  ngpVanCampaignIntegrationEnabled: boolean;
}): ReactElement {
  if (!availableGCSIntegrations.length) {
    return <div />;
  }

  const showIntegrationPicker = availableGCSIntegrations.length != 1;

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={Yup.object({
        integrationId: Yup.string().required("Required"),
        objectName: Yup.string().required("Required"),
        phoneNumberColumn: Yup.string().required("Required"),
        ...(ngpVanCampaignIntegrationEnabled
          ? { vanIdColumn: Yup.string().required("Required") }
          : {}),
      })}
      onSubmit={onSubmit || _.noop}
    >
      {({ dirty, values }) => (
        <Form>
          <RouteLeavingGuard when={dirty} />
          <FormikErrorBanner />
          {showIntegrationPicker ? (
            <FormikSelect
              label="Integration"
              name="integrationId"
              withNoneOption={false}
              fullWidth
            >
              {availableGCSIntegrations.map(integration => (
                <MenuItem value={integration.id} key={integration.id}>
                  {integration.name}
                </MenuItem>
              ))}
            </FormikSelect>
          ) : (
            <Field type="hidden" name="integrationId" />
          )}
          <ObjectNameInput
            readOnly={readOnly}
            integrationId={values.integrationId}
          />
          <ColumnFields
            integrationId={values.integrationId}
            objectName={values.objectName}
            showVanIdColumn={ngpVanCampaignIntegrationEnabled}
            readOnly={readOnly}
          />
          {onSubmit && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                paddingTop: theme.spacing(1),
              }}
            >
              <ContactImportFormSubmitButtons
                resetContactSourceSettings={resetContactSourceSettings}
              />
            </Box>
          )}
        </Form>
      )}
    </Formik>
  );
}
